/**
 * Product Gallery Lightbox
 * Desktop: full-width zoom + vertical pan.
 * Mobile: ~2.5x scale zoom + bounded drag in all directions.
 */
(function (window, document) {
  'use strict';

  var BODY_OPEN_CLASS = 'koh-pswp-open';
  var PAN_THRESHOLD = 4;
  var HI_RES_WIDTH = 2400;
  var MOBILE_MAX_WIDTH = 640;
  var MOBILE_ZOOM_SCALE = 2.5;
  var DOUBLE_TAP_DELAY = 300;
  var DOUBLE_TAP_DISTANCE = 28;

  function getHiResSrc(src) {
    if (!src) return src;
    if (src.indexOf('width=') !== -1) {
      return src.replace(/width=\d+/i, 'width=' + HI_RES_WIDTH);
    }
    return src;
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: ' + MOBILE_MAX_WIDTH + 'px)').matches;
  }

  function ProductGalleryLightbox(lightboxRoot) {
    if (!lightboxRoot) {
      throw new Error('ProductGalleryLightbox: root element is required');
    }

    this.root = lightboxRoot;
    this.el = {
      backdrop: lightboxRoot.querySelector('[data-pgl-backdrop]'),
      close: lightboxRoot.querySelector('[data-pgl-close]'),
      zoom: lightboxRoot.querySelector('[data-pgl-zoom]'),
      prev: lightboxRoot.querySelector('[data-pgl-prev]'),
      next: lightboxRoot.querySelector('[data-pgl-next]'),
      counter: lightboxRoot.querySelector('[data-pgl-counter]'),
      stage: lightboxRoot.querySelector('[data-pgl-stage]'),
      zoomWrap: lightboxRoot.querySelector('[data-pgl-zoom-wrap]'),
      img: lightboxRoot.querySelector('[data-pgl-img]'),
    };

    this.state = {
      items: [],
      index: 0,
      zoomed: false,
      scale: 1,
      panX: 0,
      panY: 0,
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      didPan: false,
      lastTapTime: 0,
      lastTapX: 0,
      lastTapY: 0,
      controlsBound: false,
      galleryBound: false,
    };

    this.options = {
      getItems: function () {
        return [];
      },
      onSlideChange: null,
      getSwipedFlag: function () {
        return false;
      },
      clearSwipedFlag: function () {},
      getActiveIndex: function () {
        return 0;
      },
      slideSelector: '.pfp__slide',
      imgSelector: 'img',
    };
  }

  ProductGalleryLightbox.getHiResSrc = getHiResSrc;
  ProductGalleryLightbox.isMobileViewport = isMobileViewport;
  ProductGalleryLightbox.MOBILE_ZOOM_SCALE = MOBILE_ZOOM_SCALE;

  ProductGalleryLightbox.prototype.configure = function (options) {
    if (!options) return this;
    var key;
    for (key in options) {
      if (Object.prototype.hasOwnProperty.call(options, key)) {
        this.options[key] = options[key];
      }
    }
    return this;
  };

  ProductGalleryLightbox.prototype.init = function () {
    this._bindControls();
    return this;
  };

  ProductGalleryLightbox.prototype.bindGallery = function (galleryRoot, options) {
    if (!galleryRoot || this.state.galleryBound) return this;

    if (options) this.configure(options);

    var self = this;
    this.state.galleryBound = true;
    galleryRoot.dataset.pglGalleryBound = 'true';

    galleryRoot.addEventListener('click', function (e) {
      if (self.options.getSwipedFlag()) {
        self.options.clearSwipedFlag();
        return;
      }

      var img = e.target.closest(self.options.slideSelector + ' ' + self.options.imgSelector);
      if (!img) return;

      e.preventDefault();

      var slide = img.closest(self.options.slideSelector);
      var track = slide && slide.parentElement;
      if (!slide || !track) return;

      var slides = Array.prototype.slice.call(track.querySelectorAll(self.options.slideSelector));
      var index = slides.indexOf(slide);
      if (index < 0) index = self.options.getActiveIndex();

      self.open(index);
    });

    return this;
  };

  ProductGalleryLightbox.prototype.open = function (startIndex) {
    var items = this.options.getItems();
    if (!items.length) return;

    this.state.items = items;
    this.state.index = Math.max(0, Math.min(startIndex || 0, items.length - 1));
    this._renderSlide();
    this._updateZoomButton();

    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_OPEN_CLASS);

    if (this.el.close) this.el.close.focus();
  };

  ProductGalleryLightbox.prototype.close = function () {
    this._resetZoom();
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_OPEN_CLASS);

    if (this.el.img) {
      this.el.img.removeAttribute('src');
      this.el.img.alt = '';
    }
  };

  ProductGalleryLightbox.prototype.step = function (delta) {
    var items = this.state.items;
    if (!items.length) return;

    this._resetZoom();
    var count = items.length;
    this.state.index = (this.state.index + delta + count) % count;
    this._renderSlide();

    if (typeof this.options.onSlideChange === 'function') {
      this.options.onSlideChange(this.state.index);
    }
  };

  ProductGalleryLightbox.prototype._usesScaleZoom = function () {
    return isMobileViewport();
  };

  ProductGalleryLightbox.prototype._getBaseDisplaySize = function () {
    if (!this.el.stage || !this.el.img) return { width: 0, height: 0 };

    var nw = this.el.img.naturalWidth;
    var nh = this.el.img.naturalHeight;
    if (!nw || !nh) return { width: 0, height: 0 };

    var stageRect = this.el.stage.getBoundingClientRect();
    var fitScale = Math.min(stageRect.width / nw, stageRect.height / nh);
    return {
      width: nw * fitScale,
      height: nh * fitScale,
    };
  };

  ProductGalleryLightbox.prototype._bindControls = function () {
    if (this.state.controlsBound) return;
    this.state.controlsBound = true;

    var self = this;

    if (this.el.backdrop) {
      this.el.backdrop.addEventListener('click', function () {
        self.close();
      });
    }

    if (this.el.close) {
      this.el.close.addEventListener('click', function () {
        self.close();
      });
    }

    if (this.el.prev) {
      this.el.prev.addEventListener('click', function () {
        self.step(-1);
      });
    }

    if (this.el.next) {
      this.el.next.addEventListener('click', function () {
        self.step(1);
      });
    }

    if (this.el.zoom) {
      this.el.zoom.addEventListener('click', function (e) {
        e.stopPropagation();
        self._toggleZoom();
      });
    }

    if (this.el.zoomWrap) {
      this.el.zoomWrap.addEventListener('click', function (e) {
        e.stopPropagation();
        self._handleImageTap(e.clientX, e.clientY);
      });

      this.el.zoomWrap.addEventListener('pointerdown', function (e) {
        self.state.didPan = false;
        if (!self.state.zoomed) return;

        self.state.isPanning = true;
        self.state.panStartX = e.clientX;
        self.state.panStartY = e.clientY;
        self.state.panOriginX = self.state.panX;
        self.state.panOriginY = self.state.panY;
        self.root.classList.add('is-panning');

        if (self.el.zoomWrap.setPointerCapture) {
          self.el.zoomWrap.setPointerCapture(e.pointerId);
        }
      });

      this.el.zoomWrap.addEventListener('pointermove', function (e) {
        if (!self.state.isPanning) return;

        var dx = e.clientX - self.state.panStartX;
        var dy = e.clientY - self.state.panStartY;

        if (Math.abs(dx) > PAN_THRESHOLD || Math.abs(dy) > PAN_THRESHOLD) {
          self.state.didPan = true;
        }

        if (self._usesScaleZoom()) {
          self.state.panX = self.state.panOriginX + dx;
          self.state.panY = self.state.panOriginY + dy;
        } else {
          self.state.panX = 0;
          self.state.panY = self.state.panOriginY + dy;
        }

        self._applyTransform();
      });

      function endPan(e) {
        if (self.state.isPanning && e) {
          var dx = e.clientX - self.state.panStartX;
          var dy = e.clientY - self.state.panStartY;
          if (Math.abs(dx) > PAN_THRESHOLD || Math.abs(dy) > PAN_THRESHOLD) {
            self.state.didPan = true;
          }
        }
        self.state.isPanning = false;
        self.root.classList.remove('is-panning');
      }

      this.el.zoomWrap.addEventListener('pointerup', endPan);
      this.el.zoomWrap.addEventListener('pointercancel', endPan);
    }

    document.addEventListener('keydown', function (e) {
      if (!self.root.classList.contains('is-open')) return;
      if (e.key === 'Escape') self.close();
      if (e.key === 'ArrowLeft') self.step(-1);
      if (e.key === 'ArrowRight') self.step(1);
    });
  };

  ProductGalleryLightbox.prototype._clearTapState = function () {
    this.state.lastTapTime = 0;
    this.state.lastTapX = 0;
    this.state.lastTapY = 0;
  };

  ProductGalleryLightbox.prototype._handleImageTap = function (clientX, clientY) {
    if (this.state.didPan) {
      this.state.didPan = false;
      this._clearTapState();
      return;
    }

    if (!this._usesScaleZoom()) {
      if (!this.state.zoomed) {
        this._zoomIn(clientX, clientY);
      } else {
        this._resetZoom();
      }
      return;
    }

    var now = Date.now();
    var dt = now - this.state.lastTapTime;
    var dx = clientX - this.state.lastTapX;
    var dy = clientY - this.state.lastTapY;
    var dist = Math.sqrt(dx * dx + dy * dy);

    if (this.state.lastTapTime && dt < DOUBLE_TAP_DELAY && dist < DOUBLE_TAP_DISTANCE) {
      this._clearTapState();
      if (this.state.zoomed) {
        this._resetZoom();
      } else {
        this._zoomIn(clientX, clientY);
      }
      return;
    }

    this.state.lastTapTime = now;
    this.state.lastTapX = clientX;
    this.state.lastTapY = clientY;
  };

  ProductGalleryLightbox.prototype._updateZoomButton = function () {
    if (!this.el.zoom) return;

    var inIcon = this.el.zoom.querySelector('.koh-pswp__zoom-icon--in');
    var outIcon = this.el.zoom.querySelector('.koh-pswp__zoom-icon--out');
    var zoomed = !!this.state.zoomed;

    if (inIcon) inIcon.hidden = zoomed;
    if (outIcon) outIcon.hidden = !zoomed;
    this.el.zoom.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
  };

  ProductGalleryLightbox.prototype._resetZoom = function () {
    this.state.zoomed = false;
    this.state.scale = 1;
    this.state.panX = 0;
    this.state.panY = 0;
    this._clearTapState();
    this.root.classList.remove('is-zoomed', 'is-panning');

    if (this.el.img) this.el.img.style.transform = '';
    if (this.el.zoomWrap) this.el.zoomWrap.style.transform = '';

    this._updateZoomButton();
  };

  ProductGalleryLightbox.prototype._getPanBounds = function () {
    if (!this.el.stage || !this.el.img || !this.state.zoomed) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    if (this._usesScaleZoom()) {
      var stageRect = this.el.stage.getBoundingClientRect();
      var base = this._getBaseDisplaySize();
      var scale = this.state.scale || MOBILE_ZOOM_SCALE;
      var scaledW = base.width * scale;
      var scaledH = base.height * scale;
      var maxPanX = Math.max(0, (scaledW - stageRect.width) / 2);
      var maxPanY = Math.max(0, (scaledH - stageRect.height) / 2);

      return {
        minX: -maxPanX,
        maxX: maxPanX,
        minY: -maxPanY,
        maxY: maxPanY,
      };
    }

    var nw = this.el.img.naturalWidth;
    var nh = this.el.img.naturalHeight;
    if (!nw || !nh) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

    var stage = this.el.stage.getBoundingClientRect();
    var imgH = stage.width * (nh / nw);

    if (imgH <= stage.height) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    return { minX: 0, maxX: 0, minY: stage.height - imgH, maxY: 0 };
  };

  ProductGalleryLightbox.prototype._clampPan = function () {
    var bounds = this._getPanBounds();
    this.state.panX = Math.max(bounds.minX, Math.min(bounds.maxX, this.state.panX));
    this.state.panY = Math.max(bounds.minY, Math.min(bounds.maxY, this.state.panY));

    if (!this._usesScaleZoom()) {
      this.state.panX = 0;
    }
  };

  ProductGalleryLightbox.prototype._applyTransform = function () {
    if (!this.el.img) return;

    if (!this.state.zoomed) {
      if (this.el.img) this.el.img.style.transform = '';
      if (this.el.zoomWrap) this.el.zoomWrap.style.transform = '';
      return;
    }

    this._clampPan();

    if (this._usesScaleZoom()) {
      if (this.el.zoomWrap) {
        this.el.zoomWrap.style.transform =
          'translate3d(' +
          this.state.panX +
          'px, ' +
          this.state.panY +
          'px, 0) scale(' +
          this.state.scale +
          ')';
      }
      this.el.img.style.transform = '';
      return;
    }

    if (this.el.zoomWrap) this.el.zoomWrap.style.transform = '';
    this.el.img.style.transform =
      'translate3d(0px, ' + this.state.panY + 'px, 0px)';
  };

  ProductGalleryLightbox.prototype._renderSlide = function () {
    var items = this.state.items;
    if (!this.el.img || !items.length) return;

    var idx = Math.max(0, Math.min(this.state.index, items.length - 1));
    this.state.index = idx;

    var item = items[idx];
    this._resetZoom();
    this.el.img.src = item.src;
    this.el.img.alt = item.alt || '';

    if (this.el.counter) {
      this.el.counter.textContent = idx + 1 + ' / ' + items.length;
    }

    if (this.el.prev) this.el.prev.hidden = items.length < 2;
    if (this.el.next) this.el.next.hidden = items.length < 2;
  };

  ProductGalleryLightbox.prototype._zoomIn = function (clientX, clientY) {
    if (!this.el.stage || !this.el.img || this.state.zoomed) return;

    this.state.zoomed = true;
    this.state.panX = 0;
    this.state.panY = 0;
    this.state.scale = this._usesScaleZoom() ? MOBILE_ZOOM_SCALE : 1;
    this.root.classList.add('is-zoomed');
    this._updateZoomButton();

    var self = this;

    function finishZoomIn() {
      var bounds = self._getPanBounds();

      if (self._usesScaleZoom() && typeof clientX === 'number' && typeof clientY === 'number') {
        var rect = self.el.stage.getBoundingClientRect();
        var clickX = clientX - rect.left;
        var clickY = clientY - rect.top;
        var ratioX = Math.max(0, Math.min(1, clickX / rect.width));
        var ratioY = Math.max(0, Math.min(1, clickY / rect.height));
        self.state.panX = bounds.maxX + ratioX * (bounds.minX - bounds.maxX);
        self.state.panY = bounds.maxY + ratioY * (bounds.minY - bounds.maxY);
      } else if (bounds.minY < 0 && typeof clientY === 'number') {
        var stageRect = self.el.stage.getBoundingClientRect();
        var y = clientY - stageRect.top;
        var clickRatio = Math.max(0, Math.min(1, y / stageRect.height));
        self.state.panY = bounds.maxY + clickRatio * (bounds.minY - bounds.maxY);
      }

      self._applyTransform();
    }

    if (this.el.img.complete && this.el.img.naturalWidth) {
      finishZoomIn();
    } else {
      this.el.img.addEventListener('load', finishZoomIn, { once: true });
    }
  };

  ProductGalleryLightbox.prototype._toggleZoom = function () {
    if (this.state.zoomed) {
      this._resetZoom();
    } else {
      this._zoomIn();
    }
  };

  window.ProductGalleryLightbox = ProductGalleryLightbox;
})(window, document);
